import { addCivilDays, isDefined } from '@mfw/common';

import type {
  FetchSleepStatisticsArgs,
  ApiStatsRange,
  ApiTimeseries,
  ApiAggregate,
} from '../types/stats.types';
import type {
  UiSleepMetricSummary,
  UiSleepStatistics,
  UiSleepPhases,
  UiSleepDelta,
} from '../../schemas/sleepStats.schema';

/** Per-night series the chart and the phase split are built from. */
export const SLEEP_NIGHTLY_KEYS = [
  'sleep_duration',
  'sleep_deep_min',
  'sleep_rem_min',
  'sleep_light_min',
  'sleep_score',
  'sleep_wakeups',
];

/**
 * Period averages. Stress is the 1–100 `stress_overall` the Daily Activity
 * evening entry writes — `stress_level` (1–5) has no entry point there.
 */
export const SLEEP_AGGREGATE_KEYS = [
  ...SLEEP_NIGHTLY_KEYS,
  'avg_sleep_hr',
  'stress_overall',
];

/** Civil days of a `[from, to)` range, oldest first. */
export function civilDaysOf(range: ApiStatsRange) {
  const days: string[] = [];
  for (let day = range.from; day < range.to; day = addCivilDays(day, 1)) {
    days.push(day);
  }
  return days;
}

function nightlyValues(timeseries: ApiTimeseries | null, metricKey: string) {
  const series = timeseries?.series.find((s) => s.metricKey === metricKey);
  return new Map(
    (series?.points ?? [])
      .filter((point) => isDefined(point.value))
      .map((point) => [point.bucket, point.value as number]),
  );
}

// `aggregate` is called with ranges [previous, current] — the BE reports
// deltas against the FIRST range, so index 1 is "current vs previous".
function summaryOf(
  aggregate: ApiAggregate | null,
  metricKey: string,
): UiSleepMetricSummary {
  const metric = aggregate?.metrics.find((m) => m.metricKey === metricKey);
  const current = metric?.values[1];
  return { avg: current?.avg ?? null, count: current?.count ?? 0 };
}

function deltaOf(
  aggregate: ApiAggregate | null,
  metricKey: string,
): UiSleepDelta | null {
  const metric = aggregate?.metrics.find((m) => m.metricKey === metricKey);
  const [previous, current] = metric?.values ?? [];
  const delta = metric?.deltas[1]?.avg;
  if (!previous?.count || !current?.count || !delta) {
    return null;
  }
  return { abs: delta.abs, pct: delta.pct };
}

function phasesOf(
  aggregate: ApiAggregate | null,
  nights: string[],
  values: {
    duration: Map<string, number>;
    deep: Map<string, number>;
    rem: Map<string, number>;
    light: Map<string, number>;
  },
): UiSleepPhases {
  let durationSum = 0;
  let deepSum = 0;
  let remSum = 0;
  let lightSum = 0;
  let excludedNights = 0;
  for (const night of nights) {
    const duration = values.duration.get(night);
    const deep = values.deep.get(night);
    const rem = values.rem.get(night);
    if (!duration || !isDefined(deep) || !isDefined(rem)) {
      continue;
    }
    // light is optional per night — when missing it stays in "not reported"
    const light = values.light.get(night) ?? 0;
    if (deep + rem + light > duration) {
      excludedNights += 1;
      continue;
    }
    durationSum += duration;
    deepSum += deep;
    remSum += rem;
    lightSum += light;
  }
  const share = (sum: number) =>
    durationSum ? Math.round((sum / durationSum) * 100) : null;
  const deepPct = share(deepSum);
  const remPct = share(remSum);
  const lightPct = share(lightSum);
  return {
    deepMin: summaryOf(aggregate, 'sleep_deep_min').avg,
    deepPct,
    excludedNights,
    lightMin: summaryOf(aggregate, 'sleep_light_min').avg,
    lightPct,
    remMin: summaryOf(aggregate, 'sleep_rem_min').avg,
    remPct,
    unreportedPct:
      isDefined(deepPct) && isDefined(remPct) && isDefined(lightPct)
        ? Math.max(0, 100 - deepPct - remPct - lightPct)
        : null,
  };
}

export function mapSleepStatisticsFromApi({
  aggregate,
  args,
  timeseries,
}: {
  args: FetchSleepStatisticsArgs;
  timeseries: ApiTimeseries | null;
  aggregate: ApiAggregate | null;
}): UiSleepStatistics {
  const duration = nightlyValues(timeseries, 'sleep_duration');
  const deep = nightlyValues(timeseries, 'sleep_deep_min');
  const rem = nightlyValues(timeseries, 'sleep_rem_min');
  const light = nightlyValues(timeseries, 'sleep_light_min');
  const score = nightlyValues(timeseries, 'sleep_score');
  const wakeups = nightlyValues(timeseries, 'sleep_wakeups');
  const pastNights = civilDaysOf(args.current).filter(
    (night) => night <= args.today,
  );

  return {
    duration: {
      ...summaryOf(aggregate, 'sleep_duration'),
      delta: deltaOf(aggregate, 'sleep_duration'),
    },
    nights: civilDaysOf(args.chart).map((night) => ({
      date: night,
      durationMin: duration.get(night) ?? null,
      inCurrent: night >= args.current.from && night < args.current.to,
      isFuture: night > args.today,
      score: score.get(night) ?? null,
      wakeups: wakeups.get(night) ?? null,
    })),
    nightsInPeriod: pastNights.length,
    phases: phasesOf(aggregate, pastNights, { deep, duration, light, rem }),
    score: summaryOf(aggregate, 'sleep_score'),
    sleepHr: summaryOf(aggregate, 'avg_sleep_hr'),
    stress: summaryOf(aggregate, 'stress_overall'),
    wakeups: summaryOf(aggregate, 'sleep_wakeups'),
  };
}
